// The caller: who they are, which organisation they are acting in, and how the
// transport gets both.
//
// mindvm's frontend once owned authentication against its own JWT endpoints;
// what is left here is small whichever provider is in force: hold a
// credential, hold an organisation, and hand both to the client.
//
// Under authit — the default — the credential is the access token mindvm's
// own `POST /auth/login` returned, and this file also holds the refresh token
// that renews it a minute before it runs out. Under Zitadel it is the access
// token oidc-client-ts holds (see oidc.ts): signing in is a redirect to the
// hosted login and back, renewal swaps the token in place, and an expiry
// nobody could renew signs out. Under the header seam it is the subject
// somebody typed.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { AUTH_MODE, configureClient, unwrap } from '../api/client';
import { login, logout, refreshSession } from '../api/gen';
import { CALLBACK_PATH, oidc, oidcConfigured } from './oidc';

interface Session {
  /** The bearer token, or the test subject when the build uses the header seam. */
  credential: string | null;
  /** False until a stored session has been restored or renewed. */
  ready: boolean;
  /** The organisation every request acts in. */
  orgId: string | null;
  setOrgId: (id: string | null) => void;
  /** Only meaningful under the header seam; a provider owns it otherwise. */
  setCredential: (value: string | null) => void;
  /** authit build: sign in with an address and a password. */
  signInPassword: (email: string, password: string) => Promise<void>;
  /** Zitadel build: redirects to the hosted login. */
  signIn: () => Promise<void>;
  /** Zitadel build: finishes the redirect back from the hosted login. */
  completeSignIn: () => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<Session | null>(null);

const CREDENTIAL_KEY = 'mindvm.credential';
const ORG_KEY = 'mindvm.org';
// authit only: the refresh token and when the access token expires.
const REFRESH_KEY = 'mindvm.refresh';
const EXPIRES_KEY = 'mindvm.expires';

// Storage can be disabled or full; a console that cannot remember still works.
function get(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function put(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // forgotten on reload, which is the worst that happens
  }
}

// One restore per page load. StrictMode runs effects twice, and a refresh
// token presented twice reads to either provider as a replay.
let restoring: Promise<string | null> | null = null;

async function restoreZitadel(): Promise<string | null> {
  const manager = oidc();
  const user = await manager.getUser();
  if (!user) return null;
  if (!user.expired) return user.access_token;
  if (!user.refresh_token) return null;
  const renewed = await manager.signinSilent();
  return renewed?.access_token ?? null;
}

interface Tokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

function storeTokens(t: Tokens | null): void {
  put(CREDENTIAL_KEY, t?.accessToken ?? null);
  put(REFRESH_KEY, t?.refreshToken ?? null);
  put(EXPIRES_KEY, t?.expiresAt ?? null);
}

/** Renews the stored pair. Null when there was nothing to renew or the server
 * refused, which is a session that has ended. */
async function renewAuthit(): Promise<Tokens | null> {
  const refreshToken = get(REFRESH_KEY);
  if (!refreshToken) return null;
  try {
    const pair = await unwrap(refreshSession({ body: { refreshToken } }));
    return pair ?? null;
  } catch {
    return null;
  }
}

/** On load: an access token that lapsed while the tab was closed is renewed
 * before anything asks the server for something with it. */
async function restoreAuthit(): Promise<string | null> {
  const access = get(CREDENTIAL_KEY);
  const expires = get(EXPIRES_KEY);
  if (access && expires && Date.parse(expires) - 30_000 > Date.now()) return access;
  const renewed = await renewAuthit();
  storeTokens(renewed);
  return renewed?.accessToken ?? null;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [credential, setCredentialState] = useState<string | null>(() => get(CREDENTIAL_KEY));
  const [ready, setReady] = useState(AUTH_MODE === 'header');
  const [expiresAt, setExpiresAt] = useState<string | null>(() => get(EXPIRES_KEY));
  const [orgId, setOrgIdState] = useState<string | null>(() => get(ORG_KEY));

  const setCredential = useCallback((value: string | null) => {
    setCredentialState(value);
    put(CREDENTIAL_KEY, value);
  }, []);

  const setOrgId = useCallback((id: string | null) => {
    setOrgIdState(id);
    put(ORG_KEY, id);
  }, []);

  const adoptTokens = useCallback((t: Tokens | null) => {
    storeTokens(t);
    setCredentialState(t?.accessToken ?? null);
    setExpiresAt(t?.expiresAt ?? null);
  }, []);

  const signOut = useCallback(() => {
    if (AUTH_MODE === 'authit') {
      // The refresh token is a row on the server, so forgetting it here would
      // leave a session nobody can end. Best effort: offline still forgets
      // locally, and the token expires on its own.
      const refreshToken = get(REFRESH_KEY);
      if (refreshToken) void logout({ body: { refreshToken } }).catch(() => undefined);
      adoptTokens(null);
    }
    setCredential(null);
    setOrgId(null);
    queryClient.clear();
    if (AUTH_MODE === 'zitadel' && oidcConfigured()) {
      // Forget oidc-client-ts's copy too. The hosted session is left to
      // Zitadel, so signing back in is one click.
      oidc().removeUser().catch(() => undefined);
    }
  }, [adoptTokens, setCredential, setOrgId, queryClient]);

  const signInPassword = useCallback(
    async (email: string, password: string) => {
      const pair = await unwrap(login({ body: { email, password } }));
      if (!pair) throw new Error('Sign-in failed.');
      // The token is opaque here and never decoded in the browser: the
      // server re-resolves the caller on every request.
      queryClient.clear();
      adoptTokens(pair);
    },
    [adoptTokens, queryClient],
  );

  const signIn = useCallback(async () => {
    await oidc().signinRedirect();
  }, []);

  const completeSignIn = useCallback(async () => {
    const user = await oidc().signinCallback();
    if (!user) throw new Error('no sign-in to complete');
    setCredential(user.access_token);
  }, [setCredential]);

  // The client reads signOut through a ref: configureClient installs its
  // interceptors once, and a 401 must reach the current callback.
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  // The sources are read at request time rather than captured, because both
  // change mid-session: switching organisation must not need a reload.
  useEffect(() => {
    configureClient(
      () => get(CREDENTIAL_KEY),
      () => {
        // A rejected token is a session that ended; show the sign-in page
        // rather than retrying with it.
        if (AUTH_MODE !== 'header') signOutRef.current();
      },
    );
  }, []);

  // authit: resume the stored session, renewing a token that lapsed while the
  // tab was closed.
  useEffect(() => {
    if (AUTH_MODE !== 'authit') return;
    let cancelled = false;
    restoring ??= restoreAuthit().catch(() => null);
    restoring.then((token) => {
      if (cancelled) return;
      setCredentialState(token);
      setExpiresAt(get(EXPIRES_KEY));
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // authit: the access token is short-lived, so it is renewed a minute
  // before it runs out rather than after a request has already failed.
  useEffect(() => {
    if (AUTH_MODE !== 'authit' || !credential || !expiresAt) return;
    const due = Date.parse(expiresAt) - 60_000 - Date.now();
    const timer = setTimeout(
      () => {
        void renewAuthit().then((pair) => {
          if (pair) adoptTokens(pair);
          else signOutRef.current();
        });
      },
      Math.max(due, 0),
    );
    return () => clearTimeout(timer);
  }, [credential, expiresAt, adoptTokens]);

  // Zitadel: resume the stored session, renewing a token that lapsed while
  // the tab was closed; keep the credential current as renewals land; and
  // sign out when a token expires that could not be renewed.
  useEffect(() => {
    if (AUTH_MODE !== 'zitadel') return;
    if (!oidcConfigured()) {
      // A build with no Zitadel: the sign-in page says so.
      setReady(true);
      return;
    }
    const manager = oidc();
    let cancelled = false;
    if (window.location.pathname === CALLBACK_PATH) {
      setReady(true);
    } else {
      restoring ??= restoreZitadel().catch(() => null);
      restoring.then((token) => {
        if (cancelled) return;
        if (token) setCredential(token);
        else if (get(CREDENTIAL_KEY)) signOutRef.current();
        setReady(true);
      });
    }
    const onLoaded = (user: { access_token: string }) => setCredential(user.access_token);
    const onExpired = () => signOutRef.current();
    manager.events.addUserLoaded(onLoaded);
    manager.events.addAccessTokenExpired(onExpired);
    return () => {
      cancelled = true;
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeAccessTokenExpired(onExpired);
    };
  }, [setCredential]);

  const value = useMemo(
    () => ({
      credential,
      ready,
      orgId,
      setOrgId,
      setCredential,
      signInPassword,
      signIn,
      completeSignIn,
      signOut,
    }),
    [credential, ready, orgId, setOrgId, setCredential, signInPassword, signIn, completeSignIn, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
